/**
 * PO / GRN display helpers — UI shows transaction numbers, never raw UUIDs.
 * Shared module cache: one GET each for PO and GRN lists per session.
 */
import { getPurchaseOrders } from '../api/procurement';
import { getGrns } from '../api/grn';

export const EMPTY_PROCUREMENT_LABEL = '—';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export function getPoId(po) {
  return po?.purchase_order_id ?? po?.id ?? '';
}

export function getGrnId(grn) {
  return grn?.grn_id ?? grn?.id ?? '';
}

export function resolvePoNumberFromList(poId, pos = []) {
  if (!poId) return EMPTY_PROCUREMENT_LABEL;
  const po = pos.find(p => getPoId(p) === poId);
  return po?.po_number || EMPTY_PROCUREMENT_LABEL;
}

export function resolveGrnNumberFromList(grnId, grns = []) {
  if (!grnId) return EMPTY_PROCUREMENT_LABEL;
  const grn = grns.find(g => getGrnId(g) === grnId);
  return grn?.grn_number || EMPTY_PROCUREMENT_LABEL;
}

export function createPoCatalog(pos = []) {
  const list = normalizeList(pos);
  const byId = new Map();

  for (const po of list) {
    const id = getPoId(po);
    if (id) byId.set(id, po);
  }

  return {
    items: list,
    byId,
    resolveById(poId) {
      if (!poId) return EMPTY_PROCUREMENT_LABEL;
      return byId.get(poId)?.po_number || EMPTY_PROCUREMENT_LABEL;
    },
  };
}

export function createGrnCatalog(grns = []) {
  const list = normalizeList(grns);
  const byId = new Map();

  for (const grn of list) {
    const id = getGrnId(grn);
    if (id) byId.set(id, grn);
  }

  return {
    items: list,
    byId,
    resolveById(grnId) {
      if (!grnId) return EMPTY_PROCUREMENT_LABEL;
      return byId.get(grnId)?.grn_number || EMPTY_PROCUREMENT_LABEL;
    },
  };
}

let cachedCatalog = null;
let loadPromise = null;

export function invalidateProcurementCatalogCache() {
  cachedCatalog = null;
  loadPromise = null;
}

/** Shared cache — parallel GET /purchase-orders/ + /grns/ for the inventory module. */
export function fetchProcurementCatalog({ force = false } = {}) {
  if (!force && cachedCatalog) return Promise.resolve(cachedCatalog);
  if (!force && loadPromise) return loadPromise;

  loadPromise = Promise.all([
    getPurchaseOrders().then(normalizeList).catch(() => []),
    getGrns().then(normalizeList).catch(() => []),
  ])
    .then(([pos, grns]) => {
      cachedCatalog = {
        pos: createPoCatalog(pos),
        grns: createGrnCatalog(grns),
      };
      return cachedCatalog;
    })
    .catch((err) => {
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}
